<script setup lang="ts">
import { computed, useId } from 'vue'

// Multi-line text field, same look as UiTextField.
const props = withDefaults(
  defineProps<{
    label: string
    placeholder?: string
    hint?: string
    error?: string
    rows?: number
    maxlength?: number
    disabled?: boolean
    id?: string
  }>(),
  { rows: 4 },
)

const model = defineModel<string>({ default: '' })

const generatedId = useId()
const inputId = computed(() => props.id ?? `textarea-${generatedId}`)
const describedBy = computed(() =>
  [props.error ? `${inputId.value}-error` : null, props.hint ? `${inputId.value}-hint` : null].filter(Boolean).join(' ') ||
  undefined,
)
</script>

<template>
  <div class="ui-field">
    <label :for="inputId" class="ui-field__label">{{ label }}</label>
    <textarea
      :id="inputId"
      v-model="model"
      class="ui-field__input ui-textarea"
      :class="{ 'ui-field__input--invalid': !!error }"
      :rows="rows"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :disabled="disabled"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
    ></textarea>
    <span v-if="error" :id="`${inputId}-error`" role="alert" class="ui-field__error">{{ error }}</span>
    <span v-if="hint" :id="`${inputId}-hint`" class="ui-field__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-field__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-field__input {
  padding: 8px 12px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font-size: var(--type-body-size);
  line-height: 1.45;
  color: var(--text-primary);
  background: var(--surface-card);
  width: 100%;
  resize: vertical;
  white-space: pre-wrap;
}
.ui-field__input::placeholder {
  color: var(--text-placeholder);
}
.ui-field__input--invalid {
  border-color: var(--error);
}
.ui-field__input:disabled {
  opacity: 0.6;
}
.ui-field__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-field__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-field__input:focus {
  outline: none;
  border-color: var(--accent);
}
</style>
